/* eslint-disable */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { message, notification } from "antd";
import moment from "moment";

import { IUseInvestorData } from "./IUseInvestorData";
import {
	ContractData,
	InvestorDTO,
	InvestorErrorsDTO,
	ContractErrorsDTO,
	Institution,
	InstitutionOption,
	TransferValues
} from "../../Models";

import { emptyContract, emptyContractErrors, emptyInvestor, emptyInvestorErrors } from "../../Assets/Data/EmptyValues";

import Utils from "../../Utils";
import { PlacementAgentValues } from "../../Models/PlacementAgent";

export const useInvestorData = ({
	translations,
	institutionsAPI,
	contractAPI,
	investorsAPI,
	templatesAPI,
	placementAgentVehicles,
	placementAgentActiveVehicles,
	userIdInstitution,
	isCreand,
	placesService,
	setSelectedPersonType,
	onVehicleChange,
	unlockNextStep,
	getPlacePredictions,
	isInvestor
}: IUseInvestorData) => {
	const navigate = useNavigate();
	const [searchParams, _] = useSearchParams();
	const location = useLocation();

	const [loading, setLoading] = useState<boolean>(false);
	const [noContent, setNoContent] = useState<boolean>(false);
	const [contractData, setContractData] = useState<ContractData>(emptyContract);
	const [error, setError] = useState<boolean>(false);
	const [templateList, setTemplateList] = useState<any>([]);
	const [investorsData, setInvestorsData] = useState<InvestorDTO[]>([]);
	const [nationalitiesList, setNationalitiesList] = useState<any[]>([]);
	const [placementAgentInstitutions, setPlacementAgentInstitutions] = useState<InstitutionOption[]>([]);
	const [transferValues, setTransferValues] = useState<TransferValues>();
	const [investorTransferOrigin, setInvestorTransferOrigin] = useState<any>();

	const [contractErrors, setContractErrors] = useState<ContractErrorsDTO>(emptyContractErrors);

	const [investorsErrors, setInvestorsErrors] = useState<InvestorErrorsDTO[]>([]);

	const [mainAddressInputs, setMainAddressInputs] = useState<string[]>([]);
	const [manualAddressVisibility, setManualAddressVisibility] = useState<Record<number, { mainResidence: boolean }>>({});

	const [activeAddress, setActiveAddress] = useState<{ index: number, field: string } | null>(null);

	const [vehicles, setVehicles] = useState<PlacementAgentValues[]>([]);

	const updateManualAddressVisibility = (
		index: number,
		field: 'mainResidence',
		visible: boolean,
	) => {
		setManualAddressVisibility((prevState) => ({
			...prevState,
			[index]: {
				[field]: visible,
			},
		}));
	};

	const normalizeDocumentType = (documentType?: string) => {
		switch ((documentType || '').toLowerCase()) {
			case 'dni':
				return 'dni';
			case 'nif':
				return 'nif';
			case 'passport':
			case 'pasaporte':
				return 'passport';
			case 'residencecard':
			case 'residence card':
			case 'tarjeta de residencia':
			case 'tarjeta de residenca':
				return 'residenceCard';
			default:
				return undefined;
		}
	};

	const inferDocumentType = (investor: InvestorDTO) => {
		if (investor.dni) return 'dni';
		if (investor.nif) return 'nif';
		if (investor.passport) return 'passport';
		if (investor.residenceCard) return 'residenceCard';
		return undefined;
	};

	const getDocumentValueByType = (investor: InvestorDTO, documentType?: string) => {
		if (!documentType) return '';

		switch (documentType) {
			case 'dni':
				return investor.dni || '';
			case 'nif':
				return investor.nif || '';
			case 'passport':
				return investor.passport || '';
			case 'residenceCard':
				return investor.residenceCard || '';
			default:
				return '';
		}
	};

	const mapInvestorDocumentFields = (investor: InvestorDTO) => {
		const normalizedDocumentType = normalizeDocumentType(investor.documentType) || inferDocumentType(investor);
		const documentValue = investor.document
			|| getDocumentValueByType(investor, normalizedDocumentType)
			|| investor.dni
			|| investor.nif
			|| investor.passport
			|| investor.residenceCard
			|| '';

		const mappedInvestor: InvestorDTO = {
			...investor,
			documentType: normalizedDocumentType,
			document: documentValue,
			dni: '',
			nif: '',
			passport: '',
			residenceCard: '',
		};

		if (normalizedDocumentType) {
			(mappedInvestor as any)[normalizedDocumentType] = documentValue;
		}

		return mappedInvestor;
	};

	useEffect(() => {
		setInvestorsData([emptyInvestor]);
		setInvestorsErrors([emptyInvestorErrors]);
		setContractData(emptyContract);
		setContractErrors(emptyContractErrors);
		setTransferValues(undefined);
		setPlacementAgentInstitutions([]);
	}, [location.pathname]);

	useEffect(() => {
		if (searchParams.has('ContractId') && !isInvestor) {
			configureInitialData(Number(searchParams.get('ContractId')));
		} 
		else if (searchParams.has('hash')) {
			const hash = searchParams.get('hash');
			const getContractIdByHash = async(hash: string) => {
				return await contractAPI.getContractByHash(hash).then((res: any) => {
					if (res.success) {
						configureInitialData(Number(res.data));
					} else {
						message.error(translations.messageError);
						setNoContent(true);
					}
				})
				.catch((err: any) => {
					message.error(translations.messageError);
				})
			}
		
			getContractIdByHash(hash!);
		}
		else {
			setInvestorsData([emptyInvestor]);
			setInvestorsErrors([emptyInvestorErrors]);
		}
		getAllTemplates();
		getNationalities();
	}, []);

	useEffect(() => {
		if (placementAgentInstitutions.length > 0 && contractData.idInvestorOrigin && investorsData && investorsData[0].idShareType) {
			const institution = placementAgentInstitutions.find((x: Institution) => x.id === contractData.idInvestorOrigin && x.idSharesType === investorsData[0].idShareType);
			setInvestorTransferOrigin(institution);
		}
	}, [placementAgentInstitutions, contractData.idInvestorOrigin, investorsData]);

	useEffect(() => {
		templateExists();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contractData.idLanguage, 
		contractData.idPersonType, 
		contractData.vehicleId
	]);

	useEffect(() => {
		if (contractData.vehicleId) {
			getPlacementAgentInstitutions();
		}
		if (contractData.vehicleId && contractData.idInvestorOrigin && investorTransferOrigin?.idSharesType) {
			contractAPI.getTransferValues(contractData.idInvestorOrigin, contractData.vehicleId, investorTransferOrigin?.idSharesType)
				.then((values: TransferValues) => {
					setTransferValues(values);
				}).catch((err: any) => console.log(err));
		}
	}, [contractData.vehicleId, contractData.idInvestorOrigin, investorTransferOrigin?.idSharesType]);

	useEffect(() => {
		if (contractData.idInvestorOrigin && contractData.commitment !== 0 && transferValues) {
			if (isTransfer() && contractData.commitment > transferValues.available + transferValues.transfered) {
				notification.error({
					message: `${translations.transferExceededTitle}`,
					description: translations.limiteExcedido,
					duration: 10
				});
				setError(true);
			}
		}
	}, [contractData.idInvestorOrigin, contractData.commitment, transferValues]);

	useEffect(() => {
		getVehicles();
	}, [placementAgentActiveVehicles, placementAgentVehicles]);
	const configureInitialData = async(ContractId: number) => {
		setLoading(true);
		await contractAPI.getContract(ContractId).then(async(contractResponse: any) => {
			if (contractResponse.success) {
				const contract = contractResponse.data;
				await investorsAPI.getInvestors(ContractId).then((resInvestors: any) => {
					if (resInvestors.success) {
						const newInvestorErrors: any[] = [];
						let investors = resInvestors.data?.map((investor: InvestorDTO, index: number) => {
							setMainAddressInputs((prevState: string[]) => {
								const updatedInputs = [...prevState];
								updatedInputs[index] = `${investor.mainResidence}, ${investor.mainResidencePostalCode}, ${investor.mainResidenceCity}, ${investor.mainResidenceCountry}`;
								return updatedInputs;
							});
							const mappedInvestor = mapInvestorDocumentFields(investor);
							newInvestorErrors.push({ ...emptyInvestorErrors});
							return mappedInvestor;
						});
						setInvestorsErrors(newInvestorErrors);
						let firstInvestor = resInvestors.data?.[0];
						let otherData;
						if (firstInvestor) {
							otherData = {
								vehicleId: Number(firstInvestor.idVehicle),
								commitment: firstInvestor.commitment,
								bp: firstInvestor.bp, 
								container: firstInvestor.container
							}
						}
						setSelectedPersonType(contract.idPersonType)
						setContractData({...contract, ...otherData});
						if (otherData?.vehicleId) {
							onVehicleChange?.(Number(otherData.vehicleId));
						}
						setInvestorsData(investors);
						setLoading(false);						
					}
				});
			} else {
				setContractData({ ...contractData, idInstitution: userIdInstitution });
				setNoContent(true);
			}
		}).catch((err: any) => console.log(err));
		setLoading(false);
	};

	const getNationalities = () => {
		investorsAPI.getCountries().then((res: any) => {
			if (res !== "") {
				setNationalitiesList(res);
			}
		}).catch((err: any) => console.log(err));
	};

	const getPlacementAgentInstitutions = () => {
		if (!contractData.vehicleId) return;
		if (userIdInstitution === 536) {
			institutionsAPI.getPlacementAgentInstitutions({idVehicle: contractData.vehicleId}).then((institutions: Institution[]) => {
				setPlacementAgentInstitutions(
					institutions.map((x: Institution) => ({
						value: x.id,
						label: `${x.name} (${x.sharesType})`,
						...x
					}))
				);
			}).catch((err: any) => console.log(err));
		} else if (isTransfer() && userIdInstitution === 1231) {
			institutionsAPI.getPlacementAgentInstitutions({idVehicle: contractData.vehicleId}).then((institutions: Institution[]) => {
				const placementAgentsInstitutions = institutions.filter((x: Institution) => x.id === 1232);
				setPlacementAgentInstitutions(
					placementAgentsInstitutions.map((x: Institution) => ({
						value: x.id,
						label: `${x.name} (${x.sharesType})`,
						...x
					}))
				);
			}).catch((err: any) => console.log(err));
		} else if (isTransfer() && userIdInstitution === 527) {
			institutionsAPI.getPlacementAgentInstitutions({idVehicle: contractData.vehicleId}).then((institutions: Institution[]) => {
				const placementAgentsInstitutions = institutions.filter((x: Institution) => x.id === 3447);
				setPlacementAgentInstitutions(
					placementAgentsInstitutions.map((x: Institution) => ({
						value: x.id,
						label: `${x.name} (${x.sharesType})`,
						...x
					}))
				);
			}).catch((err: any) => console.log(err));
		} else {
			institutionsAPI.getPlacementAgentInstitutions({idPlacementAgent: userIdInstitution, idVehicle: contractData.vehicleId}).then((institutions: Institution[]) => {
				setPlacementAgentInstitutions(
					institutions.map((x: Institution) => ({
						value: x.id,
						label: `${x.name} (${x.sharesType})`,
						...x
					}))
				);
			}).catch((err: any) => console.log(err));
		}
	};

	const postContract = async(data: any) => {
		await contractAPI.postContract(data).then(async (res: any) => {
			if(res.success && res.data){
				const investorsToPersist = investorsData.map((investorData: InvestorDTO) => mapInvestorDocumentFields(investorData));
				await postInvestors(investorsToPersist, res.data);
			}else{
				message.error(translations.messageError);
			}
			setLoading(false);
		})
		.catch((err: any) => {
			setLoading(false);
			message.error(translations.messageError);
		})
	};

	const updateContract = async(data: any) => {
		let updateData = {...data, id: contractData.id};
		await contractAPI.updateContract(updateData).then(async (res: any) => {
			if (res.success) {
				const investorsToPersist = investorsData.map((investorData: InvestorDTO) => mapInvestorDocumentFields(investorData));
				let newInvestors = investorsToPersist.filter((x: any) => !x.id);
				let investorsToUpdate = investorsToPersist.filter((x: any) => x.id && x.id !== 0);
				if(newInvestors.length > 0){
					await postInvestors(newInvestors, contractData.id);
				}
				if(investorsToUpdate.length > 0){
					await updateInvestors(investorsToUpdate, contractData.id);
				}
			}else{
				message.error(translations.messageError);
			}
		})
		.catch((err: any) => {
			message.error(translations.messageError)}
		)
		setLoading(false);
	};

	const postInvestors = async(investors: any, contractId: number) => {
		let data = investors.map((x: any, index: number) => {
			if (index === 0){
				return {
					...x,
					IdContract: contractId,
					Commitment: contractData.commitment,
					IdVehicle: contractData.vehicleId,
					IdInstitutionRef: userIdInstitution,
					BP: contractData.bp,
					Container: contractData.container	
				}
			}
			return {
				...x,
				IdContract: contractId,
				Commitment: contractData.commitment,
				IdVehicle: contractData.vehicleId,
				IdInstitutionRef: userIdInstitution,
			}
		});
		await investorsAPI.postInvestors(data).then(async (resInvestors: any) => {
			if (resInvestors.success){
				/* if (isTransfer()) {
					await investorsAPI.updateShareType(contractId, investorTransferOrigin.idSharesType)
						.then((res: any) => {
							if (res.success) {
								message.success(`Shares type for contract with id ${contractId} updated` );
							} else {
								message.error(`There was an error saving Shares type for contract with id ${contractId}`);
							}
						});
				} */
				message.success(translations.datosGuardados);
				unlockNextStep(contractId);
				navigate(`?ContractId=${contractId}`);
				setLoading(false);
			} else {
				message.error(translations.messageError);
			}
		})
	};


	const updateInvestors = async(investors: any, contractId: any) => {
		let data = investors.map((x: any) => {
			return {
				...x,
				commitment: contractData.commitment,
			}
		});
		await investorsAPI.updateInvestors(data).then((res: any) => {
			if(res.success){
				message.success(translations.datosGuardados);
				setLoading(false);
				if(contractData?.idContractState){
					unlockNextStep(contractId)
				}
			}
		})
		.catch((err: any) => {
			setLoading(false);
			message.error(translations.messageError);
		})
	};

	const getAllTemplates = async() => {
		const isTransferContract = isTransfer();
		await templatesAPI.getTemplateList(isTransferContract).then((res: any) => {
			if(res.success){
				setTemplateList(res.data);
			}
		})
		.catch((err: any) => {
			message.error('Error with templates');
		})
	};

	const isTransfer = () => location.pathname.includes('TransferContract');

	const saveData = async(numSig?: number) => {
		setLoading(true);
		if (validateData()) {
			let data = {
				numSignatories: parseInt( contractData.numSignatories.toString()),
				idPersonType: contractData.idPersonType,
				idVehicle: contractData.vehicleId,
				idLanguage: contractData.idLanguage,
				bp: contractData.bp,
				container: contractData.container,
				isTransfer: isTransfer(),
				transferReason: contractData.transferReason,
				idInvestorOrigin: contractData.idInvestorOrigin,
				quote: contractData.quote,
				idInstitution: userIdInstitution,
			}
			let updateData = {...contractData, numSignatories: numSig ? numSig : contractData.numSignatories};
			contractData.id ? await updateContract(updateData) : await postContract(data);
			setContractErrors((prevState: any) => ({
				...prevState,
				noVehicle: false,
				noCommitment: false,
				noTemplate: false
			}));
			const newInvestorsErrors = investorsErrors.map(() => emptyInvestorErrors);
			setInvestorsErrors(newInvestorsErrors);
		} else {
			setLoading(false);
		}
	};

	const validateData = () => {
		let isOK = true;
		if(isCreand) {
			const regexBP = /^[0-9]+$/;
			// regex solo de números, que sean solo 3 números
			const regexContainer = /^[0-9]{3}$/;
			if (!contractData.bp) {
				setContractErrors((prevState: any) => ({
					...prevState,
					bp: true,
				}));
				isOK = false;
			} else if (!regexBP.test(contractData.bp!.toString())) {
				setContractErrors((prevState: any) => ({
					...prevState,
					invalidBP: true
				}));
				isOK = false;
			}
			if (!contractData.container) {
				setContractErrors((prevState: any) => ({
					...prevState,
					container: true
				}));
				isOK = false;
			} else if (!regexContainer.test(contractData.container!.toString())) {
				setContractErrors((prevState: any) => ({
					...prevState,
					invalidContainer: true
				}));
				isOK = false;
			}
		}
		if (!contractData.vehicleId) {
			setContractErrors((prevState: ContractErrorsDTO) => ({ ...prevState, vehicleId: true }));
			isOK = false;
		}
		if (contractData.commitment <= 0) {
			setContractErrors((prevState: ContractErrorsDTO) => ({ ...prevState, commitment: true }));
			isOK = false;
		}
		if (isTransfer() && !contractData.transferReason) {
			setContractErrors((prevState: ContractErrorsDTO) => ({ ...prevState, transferReason: true }));
			isOK = false;
		}
		if (isTransfer() && !contractData.idInvestorOrigin) {
			setContractErrors((prevState: ContractErrorsDTO) => ({ ...prevState, idInvestorOrigin: true }));
			isOK = false;
		}
		investorsData.forEach((investor: InvestorDTO, index: number) => {
			const investorErrors: InvestorErrorsDTO = { ...emptyInvestorErrors };

			if (!investor.name) {
				investorErrors.name = true;
				isOK = false;
			} else if (investor.name && !Utils.validate('name', investor.name)) {
				investorErrors.invalidNameFormat = true;
				isOK = false;
			}
			if (!investor.lastName) {
				investorErrors.lastName = true;
				isOK = false;
			} else if (investor.lastName && !Utils.validate('name', investor.lastName)) {
				investorErrors.invalidLastNameFormat = true;
				isOK = false;
			}
			if (!investor.secondLastName && investor.nationality === 'Portugal') {
				investorErrors.secondLastName = true;
				isOK = false;
			}
			if (investor.secondLastName && !Utils.validate('name', investor.secondLastName)) {
				investorErrors.invalidSecondLastNameFormat = true;
				isOK = false;
			}
			if (!investor.birthDate) {
				investorErrors.birthDate = true;
				isOK = false;
			}
			if (!investor.nationality) {
				investorErrors.nationality = true;
				isOK = false;
			}
			if (!investor.mainResidence || !investor.mainResidenceCity || !investor.mainResidencePostalCode || !investor.mainResidenceCountry) {
				investorErrors.mainResidence = true;
				investorErrors.mainResidenceCity = !investor.mainResidenceCity;
				investorErrors.mainResidencePostalCode = !investor.mainResidencePostalCode;
				investorErrors.mainResidenceCountry = !investor.mainResidenceCountry;
				updateManualAddressVisibility(index, 'mainResidence', true);
				isOK = false;
			} else {
				updateManualAddressVisibility(index, 'mainResidence', false);
			}
			if (!investor.emailContact) {
				investorErrors.emailContact = true;
				isOK = false;
			} else if (!Utils.validate("email", investor.emailContact)) {
				investorErrors.invalidEmailFormat = true;
				isOK = false;
			}
			if (!investor.phoneNumber) {
				investorErrors.phoneNumber = true;
				isOK = false;
			}
			if (!investor.phonePrefix) {
				investorErrors.phonePrefix = true;
				isOK = false;
			}
			if (!investor.documentType) {
				investorErrors.documentType = true;
				isOK = false
			}
			if (!investor.document) {
				investorErrors.document = true;
				isOK = false;
			} else if (!/^[A-Z0-9]*$/g.test(investor.document!)) {
				investorErrors.documentFormat = true;
				isOK = false;
			}
			if (!investor.docValidThru) {
				investorErrors.documentExpiration = true;
				isOK = false;
			} else if (moment(investor.docValidThru).isBefore(moment())) {
				investorErrors.notValidExpirationDate = true;
				isOK = false;
			}
			setInvestorsErrors((prevErrors) => {
				const newErrors = [...prevErrors];
				newErrors[index] = investorErrors;
				return newErrors;
			});
	
			return isOK;
		});

		return isOK;
	};

	const getVehicleName = () => {
		const vehicles = isTransfer() ? placementAgentVehicles : placementAgentActiveVehicles;
		const vehicle = vehicles.find((x:any)=> x.vehicleId === contractData.vehicleId);
		return vehicle ? vehicle.vehicleLegalName : ''
	};

	const isAllowedAmount = (amount: number)=> {
		if (!isTransfer() && amount) {
			contractAPI.checkAllowedAmount(contractData.vehicleId, userIdInstitution, amount)
				.then((isAllowed: boolean) => {
					setError(!isAllowed);
					if (!isAllowed) {
						notification.error({
							message: `${getVehicleName()} ${translations.limiteExcedidoTitulo}`,
							description: translations.limiteExcedido,
							duration: 10
						});
					}
				}).catch(() => message.error(translations.messageError));
		} else {
			setError(false);
		}
	};

	const onChangeCreandData = (e: any, type: string) => {
		setContractData((prevState: ContractData) => ({
			...prevState,
			[type]: e,
		}));
		if (type === 'bp'){
			setContractErrors((prevState: ContractErrorsDTO) => ({
				...prevState,
				bp: false,
				invalidBP: false
			}));
		} else {
			setContractErrors((prevState: ContractErrorsDTO) => ({
				...prevState,
				container: false,
				invalidContainer: false
			}));
		}
	}

	const onDeleteInvestor = async(index: number) => {
		let investors = [...investorsData];
		let investor = investors[index];
		let numSignatories = contractData.numSignatories - 1;
		if(investor.id){
			setLoading(true)
			await investorsAPI.deleteInvestor(investor.id).then((res: any) => {
				setLoading(false)
			})
			.catch((err: any) => {
				message.error(translations.messageError);		
				setLoading(false)

			})
			if(contractData.id > 0){
				await saveData(numSignatories);
				if(searchParams.has('ContractId')){
					await configureInitialData(Number(searchParams.get('ContractId')));
				}
				else if (searchParams.has('hash')) {
					const hash = searchParams.get('hash') || '';
					await contractAPI.getContractByHash(hash).then((res: any) => {
					if (res.success) {
						configureInitialData(Number(res.data));
				}})} 
			}
		}
		investors.splice(index, 1);
		setInvestorsData(investors);
		setContractData((prevState: ContractData) => ({
			...prevState,
			numSignatories: numSignatories
		}));
		
	}
	
	const onChangePersonType = (e: any) => {
		setContractData((prevState: ContractData) => ({
			...prevState,
			idPersonType: e,
		}));
		setSelectedPersonType(e);
	};

	const onChangeLanguage = (e: any) => {
		setContractData((prevState: ContractData) => ({
			...prevState,
			idLanguage: e
		}));
	}

	const onChangeCommitment = (e: any) => {
		setContractData((prevState: ContractData) => ({
			...prevState,
			commitment: e,
		}));
		setContractErrors((prevState: ContractErrorsDTO) => ({
			...prevState,
			noCommitment: false
		}));
	};

	const onChangeQuote = (e: any) => {
		setContractData((prevState: ContractData) => ({
			...prevState,
			quote: e
		}));
	};

	const handleChangeData = (index: number) => (e: any) => {
		const { name, value } = e.target;

		if(name === 'documentType'){
			const updatedElements = investorsData.map((element: any, i: number) =>
				i === index ? { ...element, [name]: value, dni: '', nif: '', passport: '', residenceCard: '', } : element
			);
			setInvestorsData(updatedElements);
		} else {
			const updatedElements = investorsData.map((element: any, i: number) =>
			i === index ? { ...element, [name]: value } : element
			);
			setInvestorsData(updatedElements);
		}

		setInvestorsErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[name]: false
			};
			return updatedErrors;
		});
		if (
			name === 'mainResidence'
			|| name === 'mainResidenceCity'
			|| name === 'mainResidencePostalCode'
			|| name === 'mainResidenceCountry'
		) {
			setInvestorsErrors((prevState: InvestorErrorsDTO[]) => {
				const updatedErrors = [...prevState];
				updatedErrors[index || 0] = {
					...updatedErrors[index || 0],
					mainResidence: false,
				};
				return updatedErrors;
			});
		}
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[name]: value
			};
			return updatedInvestors;
		});
	};

	const handleSelectDataContract = (field: string) => (value: string) => {
		setContractErrors((prevState: ContractErrorsDTO) => {
			let updatedErrors = {...prevState};
			updatedErrors = { ...updatedErrors, [field]: false };
			return updatedErrors;
		});
		if (field === 'vehicleId') {
			onVehicleChange?.(Number(value));
		}
		setContractData((prevState: ContractData) => {
			let updatedInvestors = {...prevState};
			(updatedInvestors as any)[field] = value;
			return updatedInvestors;
		});
	};

	const handleSelectDataInvestors = (field: string, index?: number) => (value: string) => {
		setInvestorsErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false
			};
			return updatedErrors;
		});
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			const normalizedValue = field === 'documentType' ? normalizeDocumentType(value) || value : value;
			updatedInvestors[index || 0] = {
				...updatedInvestors[index || 0],
				[field]: normalizedValue,
				...(field === 'documentType' ? { dni: '', nif: '', passport: '', residenceCard: '' } : {})
			};
			return updatedInvestors;
		});
		if (field === 'mainResidenceCountry') {
			setInvestorsErrors((prevState: InvestorErrorsDTO[]) => {
				const updatedErrors = [...prevState];
				updatedErrors[index || 0] = {
					...updatedErrors[index || 0],
					mainResidence: false,
				};
				return updatedErrors;
			});
		}
	};

	const handleChangeDate = (field: string, index: number) => (date: any) => {
		setInvestorsData((prevState: InvestorDTO[]) => {
			const updatedInvestors = [...prevState];
			updatedInvestors[index] = {
				...updatedInvestors[index],
				[field]: date ? date.format('YYYY-MM-DD') : undefined
			};
			return updatedInvestors;
		});
		setInvestorsErrors((prevState: InvestorErrorsDTO[]) => {
			const updatedErrors = [...prevState];
			updatedErrors[index || 0] = {
				...updatedErrors[index || 0],
				[field]: false
			};
			return updatedErrors;
		});
	};

	const handleChangeTransferInvestorOrigin = (field: string) => (value: string, option: any) => {
		setContractErrors((prevState: ContractErrorsDTO) => {
			let updatedErrors = {...prevState};
			updatedErrors = { ...updatedErrors, [field]: false };
			return updatedErrors;
		});
		setContractData((prevState: ContractData) => {
			let updatedInvestors = {...prevState};
			(updatedInvestors as any)[field] = value;
			return updatedInvestors;
		});
		setInvestorTransferOrigin(option);
	};
	
	const addInvestor = () => {
		setInvestorsData((prevInvestorsData: any) => [
		  ...prevInvestorsData,
		  { ...emptyInvestor}
		]);	
		setContractData((prevState: ContractData) => ({
			...prevState,
			numSignatories: contractData.numSignatories + 1
		}));
		const newInvestorErrors = [...investorsErrors];
		newInvestorErrors.push({ ...emptyInvestorErrors });
		setInvestorsErrors(newInvestorErrors);
	};

	const templateExists = () => {
		const errorsCopy = {...contractErrors};
		let exists = false;

		const { idPersonType, vehicleId, idLanguage } = contractData;
		if(templateList.length === 0) return false;

		if(!idPersonType || !vehicleId || !idLanguage) return false;
		const template = templateList.find((x: any) =>
			 x.idPersonType === idPersonType
			  && x.idVehicle === vehicleId 
			  && x.idLanguage === idLanguage
		);
		
		if (!template) {
			errorsCopy.noTemplate = true;
			exists = false;
		} else {
			errorsCopy.noTemplate = false;
			exists = true;
		}
		setContractErrors(errorsCopy);
		return exists;
	};

	const getVehicles = () => {
		let filteredVehicles: PlacementAgentValues[];
		
		if (isTransfer() || contractData.id !== 0) {
			filteredVehicles = placementAgentVehicles.filter((vehicle: PlacementAgentValues) => {
				if ((userIdInstitution === 1231 || userIdInstitution === 527) && vehicle.vehicleId === 65) {
					return true;
				} else if (userIdInstitution === 536) {
					return true;
				} else {
					return false;
				}
			});
		} else {
			filteredVehicles = placementAgentActiveVehicles;
		}
		
		setVehicles(filteredVehicles);
		
		// Si solo hay un vehículo y no hay uno seleccionado, seleccionarlo automáticamente
		if (filteredVehicles.length === 1 && !contractData.vehicleId && contractData.id === 0) {
			setContractData((prevState: ContractData) => ({
				...prevState,
				vehicleId: filteredVehicles[0].vehicleId
			}));
		}
	};

	const getAddressComponent = (placeDetails: any, type: string) => {
		const component = placeDetails?.address_components?.find((component: any) => component.types.includes(type));
		return component ? component.long_name : "";
	};

	const getFormattedStreetAddress = (placeDetails: any, fallbackValue: string) => {
		const route = getAddressComponent(placeDetails, "route");
		const streetNumber = getAddressComponent(placeDetails, "street_number");
		const premise = getAddressComponent(placeDetails, "premise");
		const subpremise = getAddressComponent(placeDetails, "subpremise");
		const pointOfInterest = getAddressComponent(placeDetails, "point_of_interest");
		const neighborhood = getAddressComponent(placeDetails, "neighborhood");
		const placeName = placeDetails?.name?.trim?.() || "";
		const fallbackStreet = fallbackValue
			.split(",")[0]
			.split(" - ")[0]
			.trim();

		return [route, streetNumber].filter(Boolean).join(", ")
			|| premise
			|| subpremise
			|| placeName
			|| pointOfInterest
			|| neighborhood
			|| fallbackStreet;
	};

	const handleSelectAddress = (index: number, field: string) => (value: string, option: any) => {
		if (!activeAddress) return;
		placesService?.getDetails(
			{ placeId: option.key },
			(placeDetails: any) => {
				const address = getFormattedStreetAddress(placeDetails, value);
				const city = getAddressComponent(placeDetails, "locality") || getAddressComponent(placeDetails, "administrative_area_level_2");
				const postalCode = getAddressComponent(placeDetails, "postal_code");
				const country = getAddressComponent(placeDetails, "country");

				setInvestorsData((prevState: InvestorDTO[]) => {
					const updatedInvestors = [...prevState];
					const updatedInvestor = { ...updatedInvestors[index] };

					if (field === "mainResidence") {
						setMainAddressInputs((prevState: string[]) => {
							const updatedInputs = [...prevState];
							updatedInputs[index] = value;
							return updatedInputs;
						});
						updatedInvestor.mainResidence = address;
						updatedInvestor.mainResidenceCity = city;
						updatedInvestor.mainResidencePostalCode = postalCode;
						updatedInvestor.mainResidenceCountry = country;
					}

					updatedInvestors[index] = updatedInvestor;
					return updatedInvestors;
				});
				setInvestorsErrors((prevState: InvestorErrorsDTO[]) => {
					const updatedErrors = [...prevState];
					updatedErrors[index] = {
						...updatedErrors[index],
						mainResidence: !address || !city || !postalCode || !country,
						mainResidenceCity: !city,
						mainResidencePostalCode: !postalCode,
						mainResidenceCountry: !country,
					};
					return updatedErrors;
				});
				updateManualAddressVisibility(index, 'mainResidence', !address || !city || !postalCode || !country);
			}
		);
	};
	
	const handleSearchAddress = (index: number, field: string) => (value: string) => {
		setActiveAddress({ index, field });
		getPlacePredictions({ input: value });
	};

	const isInEditableState = () => {
		const alwaysEditable = contractData.idContractState === 1 || contractData.idContractState === 10 || contractData.idContractState === 16 || contractData.idContractState === 17;
		const editableForQualitas = contractData.idContractState === 2 || contractData.idContractState === 3 || contractData.idContractState === 7 || contractData.idContractState === 11;
		if (userIdInstitution === 536 && (alwaysEditable || editableForQualitas )) return true;
		else if (userIdInstitution !== 536 && alwaysEditable) return true;

		return false;
	};
		  
	return {
		loading,
		noContent,
		contractErrors,
		investorsErrors,
		contractData,
		investorsData,
		error,
		transferValues,
		nationalitiesList,
		placementAgentInstitutions,
		mainAddressInputs,
		manualAddressVisibility,
		activeAddress,
		vehicles,
		onChangePersonType,
		isAllowedAmount,
		onChangeCommitment,
		onChangeQuote,
		saveData,
		onChangeLanguage,
		handleChangeData,
		handleSelectDataContract,
		handleSelectDataInvestors,
		handleChangeDate,
		addInvestor,
		onDeleteInvestor,
		onChangeCreandData,
		isTransfer,
		handleChangeTransferInvestorOrigin,
		getVehicles,
		handleSelectAddress,
		handleSearchAddress,
		isInEditableState,
	};
};
